<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { router } from '@inertiajs/vue3';
    import { useQuery } from '@tanstack/vue-query';
    import { useI18n } from 'vue-i18n';
    import axios from 'axios';
    import { IdCardLanyard } from '@lucide/vue';
    import { Pencil, Users } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import EditCompany from '@/components/companies/EditCompany.vue';
    import type { Company } from '@/types';
    import { triggerDashboardTableRefetch } from '@/composables/dashboardTableRefresh';

    const props = defineProps<{
        companyId: number;
        refreshDashboard?: boolean;
    }>();

    const { t } = useI18n();
    const { hasPermission } = usePermissions();

    const canEditCompany = computed(() => hasPermission(Permission.COMPANIES_UPDATE));
    const canViewUsers = computed(() => hasPermission(Permission.USERS_VIEW));
    const canViewEmployees = computed(() => hasPermission(Permission.EMPLOYEES_VIEW));
    const hasActions = computed(() => canEditCompany.value || canViewUsers.value || canViewEmployees.value);

    const editRequested = ref(false);
    const editOpen = ref(false);

    const { data: company } = useQuery({
        queryKey: ['dashboard-company-edit', props.companyId],
        queryFn: async (): Promise<Company> => {
            const { data } = await axios.get(route('v2.companies.edit-data.get', props.companyId));
            return data;
        },
        enabled: editRequested,
        gcTime: 0,
    });

    const openEdit = () => {
        if (company.value) {
            editOpen.value = true;
            return;
        }

        editRequested.value = true;
    };

    watch(company, (value) => {
        if (value && editRequested.value) {
            editOpen.value = true;
        }
    });

    const switchCompanyAndVisit = (routeName: string) => {
        router.patch(
            route('v2.dashboard.selected.set'),
            {
                selected_company_id: props.companyId,
                redirect_route: routeName,
            },
            {
                preserveScroll: true,
            },
        );
    };

    // Capture the value now so the callback works even after this component
    // is unmounted by the Inertia page remount that follows a modal save.
    const shouldRefreshDashboard = props.refreshDashboard === true;

    const refreshDashboardTable = () => {
        if (!shouldRefreshDashboard) {
            return;
        }

        triggerDashboardTableRefetch({ forceHydration: true });
    };
</script>

<template>
    <div
        v-if="hasActions"
        class="ml-auto flex w-18 shrink-0 items-center justify-end gap-0"
        @click.stop
    >
        <Button
            v-if="canEditCompany"
            variant="ghost_primary"
            size="icon_sm"
            class="size-6 text-muted-foreground"
            :title="t('companies.js.company_edit')"
            @click="openEdit"
        >
            <Pencil class="size-3.5" />
        </Button>

        <Button
            v-if="canViewUsers"
            variant="ghost_primary"
            size="icon_sm"
            class="size-6 text-muted-foreground"
            :title="t('users.header')"
            @click="switchCompanyAndVisit('v2.users.index')"
        >
            <Users class="size-3.5" />
        </Button>

        <Button
            v-if="canViewEmployees"
            variant="ghost_primary"
            size="icon_sm"
            class="size-6 text-muted-foreground"
            :title="t('employee.employees')"
            @click="switchCompanyAndVisit('v2.salary.employees.index')"
        >
            <IdCardLanyard class="size-4" />
        </Button>

        <EditCompany
            v-if="canEditCompany && company"
            :company="company"
            :open="editOpen"
            :after-success="refreshDashboardTable"
            hide-trigger
            @update:open="editOpen = $event"
        />
    </div>
</template>
